import './pdf-viewer.css'

import { Highlighter, Info, Search, Settings, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { pdfjs } from 'react-pdf'

import { getEpisodeProgress, getPdfPageSizes, getSignedUrl, type PdfPageSize, updateEpisodeProgress } from '@/api/api'
import { resolveDirectUrl } from '@/api/client'
import { Highlight } from '@/api/services/highlight'
import { Footer } from '@/components/shared/footer'
import { Header, type ToolbarItem } from '@/components/shared/header'
import { ActionSection, RadioSection } from '@/components/ui/custom-dropdown'
import { useViewCache } from '@/hooks/useViewCache'
import { FOOTER_HEIGHT, HEADER_HEIGHT, TOOLBAR_HEIGHT } from '@/lib/constants'
import { saveViewCache } from '@/lib/db'
import { useAppStore } from '@/store/useAppStore'
import { useBookAiStore } from '@/store/useBookAiStore'
import { useProgressStore } from '@/store/useProgressStore'
import { usePdfLayoutStore } from '@/store/useReaderLayoutStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { buildAiMarkers } from '../ai/ai-scrollbar-markers'
import { collectKeySentences } from '../ai/book-ai-position'
import { BookAiLayer } from '../ai/book-ai-sheet'
import { SelectionActionBar } from '../ai/selection-action-bar'
import { applySkimHighlights, clearSkimHighlights, isSkimOverlayMutation } from '../ai/skim-highlight'
import { TermBadgeBar } from '../ai/term-badge-bar'
import {
  type PurposePassageMark,
  useBookAi,
  usePurposePassageKeys,
  usePurposePassages,
  useSkimSentences,
} from '../ai/useBookAi'
import { HighlightListDialog } from '../highlight/highlight-list-dialog'
import { HighlightSheet } from '../highlight/highlight-sheet'
import { usePdfHighlight } from '../hooks/usePdfHighlight'
import { usePdfSettingsQuery } from '../hooks/usePdfSettingsQuery'
import { useReaderJumpKey } from '../hooks/useReaderJumpKey'
import { useRememberedLayout } from '../hooks/useRememberedLayout'
import { CustomScrollbar } from '../shared/custom-scrollbar'
import { LayoutPrompt } from '../shared/layout-prompt'
import { ReaderLayoutFields } from '../shared/reader-layout-fields'
import { SliderColor } from '../shared/slider-color-config'
import { PdfDocument } from './components/pdf-document'
import { PdfSearchSheet } from './components/pdf-search-sheet'
import {
  applyReaderLayout,
  DEFAULT_PDF_SETTINGS,
  getPdfThemeStyle,
  isPdfTinted,
  type PdfSettings,
  ScrollMode,
  TextDirection,
  toPdfLayout,
  toReaderLayout,
} from './components/pdf-settings'
import { PdfSettingsDialog } from './components/pdf-settings-dialog'

// 閲覧キャッシュ保存を開始するまでの待ち時間 (ms)
// 開いた直後は表示に必要な Range 取得を優先させる
const VIEW_CACHE_SAVE_DELAY_MS = 5000
// 流し読みの強調を探す範囲（今のページの前後この枚数にかかる章の重要文だけを使う）
const SKIM_PAGE_MARGIN = 3
// ページの描画が落ち着いてから強調をかけ直すまでの待ち時間 (ms)
const SKIM_REAPPLY_DELAY_MS = 300
// 読む目的の断片（1 ページ前後の本文）を照合するページの範囲。断片が始まるページからこの枚数先まで
// （本の中で同じ書き出しの文が別のページにもあると、そちらに誤って印が付くため）
const PASSAGE_PAGE_SPAN = 2

const passagesForPage = (passages: PurposePassageMark[], page: number): string[] =>
  passages.filter((p) => page >= Math.floor(p.key) && page <= Math.floor(p.key) + PASSAGE_PAGE_SPAN).map((p) => p.text)

type Props = {
  url: string
  title: string
  episodeId: number
  episodeIndex?: number
  numPages: number
  onInfoClick?: () => void
  onClose: () => void
  onNext?: () => void
  onPrev?: () => void
  productId?: number
  isFavorite?: boolean
  onFavoriteToggle?: () => void
  onDelete?: () => void
  /** オフラインBlob URL（親側で管理する場合のみ指定） */
  offlineBlobUrl?: string
}

export function PdfReader({
  url,
  title,
  episodeId,
  episodeIndex,
  numPages,
  onInfoClick,
  onClose,
  onNext,
  onPrev,
  productId,
  isFavorite,
  onFavoriteToggle,
  onDelete,
  offlineBlobUrl,
}: Props) {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  // フッター固定（ページ番号と進捗%を常時表示）
  const isFooterPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const pdfTheme = useUISettingsStore((state) => state.pdfTheme)
  const pdfWarmth = useUISettingsStore((state) => state.pdfWarmth)
  const pdfScrollLock = useUISettingsStore((state) => state.pdfScrollLock)
  const pageTurnAnimation = useUISettingsStore((state) => state.pageTurnAnimation)
  const themeStyle = useMemo(() => getPdfThemeStyle(pdfTheme, pdfWarmth), [pdfTheme, pdfWarmth])

  // 閲覧キャッシュから読み込み
  const { cachedBlobUrl, isLoading: cacheLoading } = useViewCache(episodeId)

  // UI状態
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false)
  const [menuVisible, setMenuVisible] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(numPages)
  // ビューアが読み込んだ PDF。検索で再ダウンロードしないよう使い回す
  const [pdfDoc, setPdfDoc] = useState<pdfjs.PDFDocumentProxy | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchActiveMatch, setSearchActiveMatch] = useState<{ pageIndex: number; matchIndexInPage: number } | null>(
    null,
  )

  // URL に無い項目は、作品ごとに覚えた設定 → 最後に選んだ設定 → 初期値の順に使う
  const {
    initialSettings,
    isPromptOpen: isLayoutPromptOpen,
    closePrompt: closeLayoutPrompt,
    save: saveLayout,
  } = useRememberedLayout(usePdfLayoutStore, productId, DEFAULT_PDF_SETTINGS)

  // PDF設定（URL クエリで保持し、リロード・共有時に復元できるようにする）
  const [pdfSettings, setPdfSettings] = usePdfSettingsQuery(initialSettings)

  const applySettings = useCallback(
    (next: PdfSettings) => {
      setPdfSettings(next)
      saveLayout(toPdfLayout(next))
    },
    [setPdfSettings, saveLayout],
  )
  const handleSettingsChange = useCallback(
    (next: PdfSettings) => {
      applySettings(next)
      closeLayoutPrompt()
    },
    [applySettings, closeLayoutPrompt],
  )
  const handleLayoutPromptDone = useCallback(() => {
    saveLayout(toPdfLayout(pdfSettings))
    closeLayoutPrompt()
  }, [saveLayout, pdfSettings, closeLayoutPrompt])
  const [effectiveZoom, setEffectiveZoom] = useState<number>()
  const documentRef = useRef<HTMLDivElement & { jumpToPage?: (p: number) => void }>(null)

  // ハイライト機能
  const {
    highlights,
    highlightSheetOpen,
    highlightListOpen,
    pendingHighlight,
    editingHighlight,
    selection,
    setHighlights,
    setHighlightSheetOpen,
    setHighlightListOpen,
    setPendingHighlight,
    setEditingHighlight,
    handleSaveHighlight,
    handleUpdateHighlight,
    handleDeleteHighlight,
    handleHighlightClick,
    handleSelectionHighlight,
    clearSelection,
  } = usePdfHighlight({
    episodeId,
    currentPage,
  })

  // PDF に渡す URL
  // - offlineBlobUrl / cachedBlobUrl があれば blob: URL（オフライン再生）
  // - 無ければ署名付き HTTPS URL を取得し、pdf.js に直接渡して Range で progressive load
  //   （linearize 済み PDF の Fast Web View を活かす）
  const [pdfUrl, setPdfUrl] = useState<string | null>(offlineBlobUrl ?? cachedBlobUrl ?? null)
  const [initialPage, setInitialPage] = useState<number | undefined>(undefined)
  const [isProgressLoaded, setIsProgressLoaded] = useState(false)

  // 進捗保存用
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedPageRef = useRef<number>(0)

  // AI 読書アシスト（位置キーは 0 始まりのページ番号）
  const [aiSheetOpen, setAiSheetOpen] = useState(false)
  const skimMode = useUISettingsStore((state) => state.readerSkimMode)
  const setChatQuote = useBookAiStore((state) => state.setChatQuote)
  const bookAi = useBookAi(episodeId, currentPage)
  const skimSentences = useSkimSentences(bookAi, SKIM_PAGE_MARGIN)
  const purposePassages = usePurposePassages(bookAi, SKIM_PAGE_MARGIN)
  const purposeKeys = usePurposePassageKeys(episodeId)
  // 流し読み中は、AI の重要文があるページをシークバーに印で出す（そのページで最も重要な文の重要度で強弱を付ける）
  // 読む目的に関係する箇所があるページにも紫の印を出す
  const skimLevel = useUISettingsStore((state) => state.readerSkimLevel)
  const aiMarkers = useMemo(
    () =>
      buildAiMarkers({
        purposeKeys,
        sentences: skimMode ? collectKeySentences(bookAi.sections, skimLevel) : [],
        toValue: Math.floor,
      }),
    [skimMode, skimLevel, bookAi.sections, purposeKeys],
  )

  // 流し読み: 描画済みページの文字レイヤーから重要文を探し、ページの上に傍線と印を描く（ページの描画・破棄に合わせて描き直す）
  useEffect(() => {
    // 読み込み中の画面ではビューアがまだ無いので、PDF の読み込み完了を待つ
    const container = documentRef.current
    const marks = { sentences: skimMode ? skimSentences : [], passages: purposePassages }
    if ((marks.sentences.length === 0 && marks.passages.length === 0) || !pdfDoc || !container) {
      clearSkimHighlights(window)
      return
    }
    const apply = () => {
      for (const layer of Array.from(container.querySelectorAll('.react-pdf__Page__textContent'))) {
        const index = layer.closest('[data-page-index]')?.getAttribute('data-page-index')
        const passages = index ? passagesForPage(purposePassages, Number(index)) : []
        applySkimHighlights(window, { sentences: marks.sentences, passages }, [layer])
      }
    }
    apply()
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new MutationObserver((records) => {
      // 自分で描いた傍線と印の差し替えには反応しない（描き直しが連鎖するため）
      if (isSkimOverlayMutation(records)) return
      clearTimeout(timer)
      timer = setTimeout(apply, SKIM_REAPPLY_DELAY_MS)
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      clearTimeout(timer)
      clearSkimHighlights(window)
    }
  }, [skimMode, skimSentences, purposePassages, pdfDoc])
  const hasCountedPlayRef = useRef(false)
  // 閲覧キャッシュへ保存済みの episodeId（同じエピソードを何度も保存しないため）
  const viewCacheSavedEpisodeIdRef = useRef<number | null>(null)

  // 初期進捗を取得（?at= があればそのページから開く。本棚への質問・関連の提案から）
  const jumpKey = useReaderJumpKey()
  useEffect(() => {
    const fetchProgress = async () => {
      if (jumpKey != null) {
        setInitialPage(Math.floor(jumpKey))
        setIsProgressLoaded(true)
        return
      }
      try {
        const progress = await getEpisodeProgress(episodeId)
        const page = progress?.progress ?? 0
        setInitialPage(page)
        lastSavedPageRef.current = page
      } catch {
        setInitialPage(0)
      } finally {
        setIsProgressLoaded(true)
      }
    }
    fetchProgress()
  }, [episodeId, jumpKey])

  // 全ページのサイズ（undefined: 取得中 / null: 取得失敗で pdf.js から求める）
  const [serverPageSizes, setServerPageSizes] = useState<PdfPageSize[] | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    getPdfPageSizes(episodeId)
      .then((sizes) => {
        if (!cancelled) setServerPageSizes(sizes)
      })
      .catch(() => {
        if (!cancelled) setServerPageSizes(null)
      })
    return () => {
      cancelled = true
    }
  }, [episodeId])

  // キャッシュ読み込み完了後に pdfUrl を更新
  useEffect(() => {
    if (cachedBlobUrl && !pdfUrl) {
      setPdfUrl(cachedBlobUrl)
    }
  }, [cachedBlobUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  // 署名付き URL はキャッシュ確認の完了を待たずに並行して取得しておく
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null)
  useEffect(() => {
    if (offlineBlobUrl) return

    let cancelled = false
    ;(async () => {
      try {
        const signedPath = await getSignedUrl(url)
        if (cancelled) return
        setSignedPdfUrl(resolveDirectUrl(signedPath))
      } catch (error) {
        console.error('[PdfReader] failed to resolve signed URL:', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [url, offlineBlobUrl])

  // キャッシュが無い場合は署名付き URL を pdf.js に直接渡す（streaming）
  useEffect(() => {
    if (pdfUrl || cacheLoading || !signedPdfUrl) return
    setPdfUrl(signedPdfUrl)
  }, [pdfUrl, cacheLoading, signedPdfUrl])

  // 進捗を保存
  const saveProgress = useCallback(
    async (page: number) => {
      if (page === lastSavedPageRef.current) return
      try {
        const isCompleted = totalPages > 0 && page >= totalPages - 1
        const shouldIncrementPlayCount = isCompleted && !hasCountedPlayRef.current
        if (shouldIncrementPlayCount) {
          hasCountedPlayRef.current = true
        }
        // 一覧画面（ProductContainer など）の表示を即座に合わせる
        useProgressStore.getState().applyEpisodeProgress(episodeId, page, isCompleted)
        await updateEpisodeProgress(episodeId, {
          progress: page,
          is_completed: isCompleted,
          increment_play_count: shouldIncrementPlayCount,
        })
        lastSavedPageRef.current = page
      } catch (error) {
        console.error('Failed to save PDF progress:', error)
      }
    },
    [episodeId, totalPages],
  )

  // ページ変更ハンドラー（デバウンス付き進捗保存）
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      setCurrentPage(pageIndex)
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = setTimeout(() => saveProgress(pageIndex), 1000)
    },
    [saveProgress],
  )

  const handleDocumentLoad = useCallback((np: number, pdf: pdfjs.PDFDocumentProxy) => {
    setTotalPages(np)
    setPdfDoc(pdf)
  }, [])

  // ファイル全体の取得率 (%)。整数に丸めて、チャンクごとの再レンダーを抑える
  const [loadPercent, setLoadPercent] = useState(0)
  const handleLoadProgress = useCallback(({ loaded, total }: { loaded: number; total: number }) => {
    if (!total) return
    setLoadPercent(Math.min(100, Math.floor((loaded / total) * 100)))
  }, [])
  const isLocalFile = !!pdfUrl?.startsWith('blob:')
  const isFileLoading = loadPercent < 100 && !isLocalFile
  const loadStatusLabel = isLocalFile ? 'キャッシュ済' : isFileLoading ? `読込 ${loadPercent}%` : '読込済'

  // ネットワークから progressive load した PDF を閲覧キャッシュへ保存する
  // （2回目以降は blob: URL で即座に開けるようにするため）
  // pdfDoc.getData() は pdf.js が未取得のチャンクだけを追加ダウンロードするので、
  // 表示のために取得済みのバイトが二重に流れることはない
  useEffect(() => {
    if (!pdfDoc) return
    if (viewCacheSavedEpisodeIdRef.current === episodeId) return
    // blob: URL はキャッシュ / オフライン保存由来なので再保存不要
    if (!pdfUrl || pdfUrl.startsWith('blob:')) return

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const data = await pdfDoc.getData()
        if (cancelled) return
        setLoadPercent(100)
        // getData() の戻り値は Uint8Array<ArrayBufferLike> で BlobPart を満たさないため、
        // ArrayBuffer 裏付けの Uint8Array に詰め替える
        const blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' })
        await saveViewCache(episodeId, blob, 'application/pdf')
        viewCacheSavedEpisodeIdRef.current = episodeId
      } catch (error) {
        console.error('[PdfReader] failed to save view cache:', error)
      }
    }, VIEW_CACHE_SAVE_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [pdfDoc, pdfUrl, episodeId])

  // テキスト選択ハンドラー（デスクトップ — ハイライト作成用）
  // ハイライト一覧からジャンプ
  const handleHighlightNavigate = useCallback(
    (highlightId: number) => {
      const highlight = highlights.find((h: Highlight) => h.id === highlightId)
      if (!highlight) return
      const areas = highlight.position_data.highlightAreas as { pageIndex: number }[] | undefined
      if (areas?.[0]) {
        // PdfDocument 内の jumpToPage を呼び出す
        const container = document.querySelector('.pdf-document-container') as HTMLDivElement & {
          jumpToPage?: (p: number) => void
        }
        container?.jumpToPage?.(areas[0].pageIndex)
      }
    },
    [highlights],
  )

  // ページジャンプ
  const jumpToPage = useCallback((pageIndex: number) => {
    const container = document.querySelector('.pdf-document-container') as HTMLDivElement & {
      jumpToPage?: (p: number) => void
    }
    container?.jumpToPage?.(pageIndex)
  }, [])

  // キーボードナビゲーション
  useEffect(() => {
    const isRtl = pdfSettings.direction === TextDirection.RightToLeft

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault()
        jumpToPage(Math.min(currentPage + 1, totalPages - 1))
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault()
        jumpToPage(Math.max(currentPage - 1, 0))
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        if (isRtl) jumpToPage(Math.max(currentPage - 1, 0))
        else jumpToPage(Math.min(currentPage + 1, totalPages - 1))
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        if (isRtl) jumpToPage(Math.min(currentPage + 1, totalPages - 1))
        else jumpToPage(Math.max(currentPage - 1, 0))
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [jumpToPage, currentPage, totalPages, pdfSettings.direction])

  // 検索コールバック
  const handleSearchKeywordChange = useCallback((keyword: string) => {
    setSearchKeyword(keyword)
    setSearchActiveMatch(null)
  }, [])

  // ドロップダウンセクション
  const dropdownSections: (RadioSection | ActionSection)[] = useMemo(
    () => [
      {
        type: 'action' as const,
        label: 'Actions',
        items: [
          { key: 'info', label: 'Open info', onClick: onInfoClick },
          ...(productId && onFavoriteToggle
            ? [
                {
                  key: 'favorite',
                  label: isFavorite ? '⭐ お気に入り解除' : '☆ お気に入り追加',
                  onClick: onFavoriteToggle,
                },
              ]
            : []),
          ...(productId && onDelete && hasFullAccess ? [{ key: 'delete', label: '🗑️ 削除', onClick: onDelete }] : []),
        ],
      },
    ],
    [onInfoClick, productId, onFavoriteToggle, isFavorite, onDelete, hasFullAccess],
  )

  // ツールバーアイテム
  const toolbarItems: ToolbarItem[] = useMemo(
    () => [
      {
        icon: <Search size={20} />,
        label: '検索',
        onClick: () => {
          setSearchPopoverOpen(true)
          setMenuVisible(false)
        },
      },
      {
        icon: <Highlighter size={20} />,
        label: 'ハイライト',
        onClick: () => {
          setHighlightListOpen(true)
          setMenuVisible(false)
        },
      },
      {
        icon: <Sparkles size={20} />,
        label: 'AI',
        onClick: () => {
          setAiSheetOpen(true)
          setMenuVisible(false)
        },
      },
      {
        icon: <Settings size={20} />,
        label: '設定',
        onClick: () => setSettingsDialogOpen(true),
      },
      {
        icon: <Info size={20} />,
        label: '情報',
        onClick: () => onInfoClick?.(),
        show: !!onInfoClick,
      },
    ],
    [onInfoClick, setHighlightListOpen],
  )

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      // blob: URL のみ revoke（HTTP URL はブラウザが管理）
      // offlineBlobUrl は親側で管理しているため revoke しない
      if (pdfUrl && pdfUrl.startsWith('blob:') && pdfUrl !== offlineBlobUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl, offlineBlobUrl])

  // フッター固定時は常に表示。非固定時はメニュー表示に追従する
  const isFooterVisible = isFooterPinned || (menuVisible && !searchPopoverOpen)

  // キャッシュ確認中 / 進捗読込中 / 署名URL 取得中 / ページサイズ取得中
  // blob: は手元のデータからサイズをすぐ求められるので、オフライン時に API を待たない
  const isWaitingPageSizes = serverPageSizes === undefined && !pdfUrl?.startsWith('blob:')
  if (cacheLoading || !isProgressLoaded || !pdfUrl || isWaitingPageSizes) {
    return (
      <div className="fixed inset-0 bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-gray-400 text-sm">読み込み中...</div>
      </div>
    )
  }

  return (
    <div
      className="w-screen h-screen fixed"
      style={themeStyle}
      data-pdf-theme={pdfTheme}
      data-pdf-tinted={isPdfTinted(pdfTheme, pdfWarmth) || undefined}
    >
      <div
        ref={documentRef}
        style={{ height: isFooterPinned ? `calc(100% - ${FOOTER_HEIGHT})` : '100%' }}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('[data-highlight-area]')) return
          if (
            target.closest('[data-reader-header]') ||
            target.closest('[data-reader-toolbar]') ||
            target.closest('[data-reader-footer]') ||
            target.closest('[data-reader-slider]')
          )
            return
          setMenuVisible((prev) => !prev)
        }}
      >
        <PdfDocument
          file={pdfUrl}
          settings={pdfSettings}
          scrollLock={pdfScrollLock}
          animatePageTurn={pageTurnAnimation}
          initialPage={initialPage}
          knownPageSizes={serverPageSizes}
          highlights={highlights}
          searchKeyword={searchKeyword}
          searchMatchIndex={searchActiveMatch?.matchIndexInPage}
          searchMatchPage={searchActiveMatch?.pageIndex}
          onPageChange={handlePageChange}
          onDocumentLoad={handleDocumentLoad}
          onLoadProgress={handleLoadProgress}
          onHighlightClick={handleHighlightClick}
          onZoomChange={(zoom) => setPdfSettings((prev) => ({ ...prev, zoom }))}
          onEffectiveZoomChange={setEffectiveZoom}
        />
      </div>

      {isFileLoading && (
        <div
          role="progressbar"
          aria-label="PDF の読み込み"
          aria-valuenow={loadPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="absolute top-0 left-0 right-0 z-30 h-0.5 pointer-events-none bg-white/10"
        >
          <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${loadPercent}%` }} />
        </div>
      )}

      {/* Header + Toolbar */}
      <div
        data-reader-header
        className="absolute top-0 left-0 right-0 z-20 will-change-transform"
        style={{
          transform: menuVisible && !searchPopoverOpen ? 'translate3d(0, 0, 0)' : 'translate3d(0, -100%, 0)',
          pointerEvents: menuVisible && !searchPopoverOpen ? 'auto' : 'none',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Header
          title={episodeIndex !== undefined ? `${episodeIndex}話 ${title}` : title}
          onClose={onClose}
          dropdownSections={dropdownSections}
          toolbarItems={toolbarItems}
        />
        <TermBadgeBar bookAi={bookAi} />
      </div>

      {/* Footer */}
      <div
        data-reader-footer
        className="absolute bottom-0 left-0 right-0 z-20 will-change-transform"
        style={{
          transform: isFooterVisible ? 'translate3d(0, 0, 0)' : 'translate3d(0, 100%, 0)',
          pointerEvents: isFooterVisible ? 'auto' : 'none',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Footer
          currentIndex={currentPage}
          totalImages={totalPages}
          subtitle={loadStatusLabel}
          onPrev={onPrev}
          onNext={onNext}
          pinnable
        />
      </div>

      {/* ページスライダー */}
      {totalPages > 1 && (
        <CustomScrollbar
          data-reader-slider
          scrollValue={currentPage}
          onScrollChange={(value) => jumpToPage(value)}
          max={totalPages - 1}
          color={SliderColor.BLUE}
          markers={aiMarkers}
          orientation={pdfSettings.scrollMode === ScrollMode.Horizontal ? 'horizontal' : 'vertical'}
          className="will-change-transform z-30"
          {...(pdfSettings.scrollMode === ScrollMode.Horizontal
            ? { bottom: FOOTER_HEIGHT, left: 0, right: 0, height: '48px' }
            : { top: `calc(${HEADER_HEIGHT} + ${TOOLBAR_HEIGHT})`, bottom: FOOTER_HEIGHT, right: 0 })}
          style={{
            transform:
              menuVisible && !searchPopoverOpen
                ? 'translate3d(0, 0, 0)'
                : pdfSettings.scrollMode === ScrollMode.Horizontal
                  ? 'translate3d(0, 100%, 0)'
                  : 'translate3d(100%, 0, 0)',
            opacity: menuVisible && !searchPopoverOpen ? 1 : 0,
            pointerEvents: menuVisible && !searchPopoverOpen ? 'auto' : 'none',
            transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease',
          }}
          inverted={
            pdfSettings.scrollMode !== ScrollMode.Horizontal || pdfSettings.direction === TextDirection.RightToLeft
          }
        />
      )}

      <BookAiLayer
        bookAi={bookAi}
        open={aiSheetOpen}
        onOpenChange={setAiSheetOpen}
        onNavigate={(key) => {
          jumpToPage(Math.floor(key))
          setMenuVisible(false)
        }}
        suppressChip={menuVisible}
      />

      {/* ハイライト一覧ダイアログ */}
      <HighlightListDialog
        open={highlightListOpen}
        onClose={() => setHighlightListOpen(false)}
        episodeId={episodeId}
        onNavigate={handleHighlightNavigate}
        onHighlightsChange={setHighlights}
      />

      {/* ハイライト作成・編集シート */}
      <HighlightSheet
        open={highlightSheetOpen}
        onClose={() => {
          setHighlightSheetOpen(false)
          setPendingHighlight(null)
          setEditingHighlight(null)
        }}
        selectedText={pendingHighlight?.selectedText ?? ''}
        highlight={editingHighlight}
        onSave={handleSaveHighlight}
        onUpdate={handleUpdateHighlight}
        onDelete={handleDeleteHighlight}
      />

      {/* PDF設定ダイアログ */}
      <PdfSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        settings={pdfSettings}
        onSettingsChange={handleSettingsChange}
        effectiveZoom={effectiveZoom}
      />

      {isLayoutPromptOpen && (
        <LayoutPrompt onDone={handleLayoutPromptDone}>
          <ReaderLayoutFields
            value={toReaderLayout(pdfSettings)}
            onChange={(layout) => applySettings(applyReaderLayout(pdfSettings, layout))}
            compact
          />
        </LayoutPrompt>
      )}

      {/* 検索シート */}
      <PdfSearchSheet
        open={searchPopoverOpen}
        onClose={() => setSearchPopoverOpen(false)}
        pdfDoc={pdfDoc}
        onKeywordChange={handleSearchKeywordChange}
        onJumpToPage={jumpToPage}
        onCurrentMatchChange={(pageIndex, matchIndexInPage) => setSearchActiveMatch({ pageIndex, matchIndexInPage })}
      />

      {/* 文字を選択したときの操作（ハイライト・AIに質問） */}
      {selection && !highlightSheetOpen && (
        <SelectionActionBar
          text={selection.selectedText}
          bookAi={bookAi}
          onHighlight={handleSelectionHighlight}
          onAskAi={() => {
            setChatQuote(selection.selectedText)
            clearSelection()
            setAiSheetOpen(true)
          }}
        />
      )}
    </div>
  )
}
